/**
 * Defines the `Context` class, which provides various scopeable utilities for use by core and plugin code.
 * @module core/base/Context
 */
import Backbone from "backbone";
import _ from "underscore";
import extend from "backbone-extend-standalone";
import UrlBuilder from "@HBKEngineering/url-builder";
import BoundOperationSet from "core/models/BoundOperationSet";
import Operation from "core/models/Operation";
import s from "underscore.string";
import BaseRouter from "core/base/Router";
/**
 * Attaches a read-only property to `thisObject` with the given value.
 * @param thisObject The owner of the property.
 * @param {String} propName The name of the property.
 * @param value The value of the property.
 */
var readOnlyProperty = function (thisObject, propName, value) {
  Object.defineProperty(thisObject, propName, {
    get: function () {
      return value;
    },
  });
};

function Context(slug, root) {
  _.extend(this, Backbone.Events);

  var defineProperty = _.partial(readOnlyProperty, this);

  defineProperty("urlBuilder", this.createUrlBuilder(slug, root));
  defineProperty("operations", this.createOperations());

  this.initialize();
}

//collection - model
//modelOperations - model

//getConfiguration() - map
//getMap - map
//getSelection - map
//createDetailWindow - map
//setResults - map
//refreshDetailWindow - map

Context.prototype = {
  /**
   * Initializes this `Context`.
   */
  initialize: function () {},

  /**
   * Creates the `BoundOperationSet` to be provided by this instance.
   * @protected
   * @return {app/models/BoundOperationSet} The operation set.
   */
  createOperations: function () {
    var result = new BoundOperationSet();

    var operations = [
      new Operation({
        id: "back",
        icon: "circle-arrow-left",
        text: "Back to List",
        action: function (model) {
          BaseRouter.current.back();

          return Operation.resolve(model);
        },
      }),
    ];

    _.each(
      operations,
      function (operation) {
        result.add(operation.bind());
      },
      this
    );

    return result;
  },

  /**
   * Creates the `UrlBuilder` associated with this `Context`.
   * @protected
   * @param {string} slug The slug for the UrlBuilder.
   * @param {String} root The root URL for the UrlBuilder.
   */
  createUrlBuilder: function (slug, root) {
    return new UrlBuilder(slug, root);
  },

  /**
   * Creates a simple navigation operation.
   * @param  {String} text        The identifier for the operation and the display text to associate with the operation.
   * @param  {String} icon        The icon identifier.
   * @param  {String} relativeUrl The url to be sent to `App.navigate` if the operation is invoked. Processed by `this.urlBuilder.url()` before being sent to the navigation registration.
   * @param  {Boolean|Function} [available]   Either the value to return from the `available` function of the `Operation` or the function itself. Default is `true`.
   * @return {app/models/BoundOperation}             The generated operation, bound to the app associated with this context and this context.
   */
  createNavigationOperation: function (text, icon, relativeUrl, available) {
    var self = this;
    var _available = _.isFunction(available)
      ? available
      : function () {
          return _.isBoolean(available) ? available : true;
        };

    var operation = new Operation({
      id: s.slugify(text),
      icon: icon,
      text: text,
      action: function (model) {
        BaseRouter.relativeNavigate(
          self.urlBuilder.url(relativeUrl),
          model || {
            trigger: true,
          }
        );

        return Operation.resolve(model);
      },
      available: _available,
    });

    return operation.bind();
  },

  /**
   * Creates a `BoundOperation` bound to the `app/App` associated with this context and this context.
   * @param text The identifier for the operation and the display text to associate with the operation.
   * @param icon The icon identifier.
   * @param {Function} action The function to execute as the action. Should be of the form `function(app, context, model) -> Promise`.
   * @param  {Boolean|Function} [available]   Either the value to return from the `available` function of the `Operation` or the function itself. Default is `true`.
   * @return {app/models/BoundOperation}             The generated operation, bound to the app associated with this context and this context.
   */
  createOperation: function (text, icon, action, available) {
    var _available = _.isFunction(available)
      ? available
      : function () {
          return _.isBoolean(available) ? available : true;
        };

    var operation = new Operation({
      id: s.slugify(text),
      icon: icon,
      text: text,
      action: action,
      available: _available,
    });

    return operation.bind();
  },

  /**
   * A convenience function, equivalent to `this.operations.get(operationId)`.
   * @param operationId The identifier of the operation.
   * @return {BoundOperation} The operation with the given identifier.
   */
  getOperation: function (operationId) {
    return this.operations.get(operationId);
  },

  /**
   * A convenience function, equivalent to `this.urlBuilder.url(path, leadingSlash)`.
   * @see UrlBuilder.url
   */
  url: function (path, leadingSlash) {
    return this.urlBuilder.url(path, leadingSlash);
  },

  /**
   * A reference to the `UrlBuilder` associated with this `Context`.
   * @type UrlBuilder
   * @readonly
   */
  urlBuilder: null,
  /**
   * A reference to the set of operations associated with this `Context`.
   * @type app/models/BoundOperationSet
   * @readonly
   */
  operations: null,
};

Context.extend = extend;

export default Context;



//////////////////
// WEBPACK FOOTER
// ./public/js/core/base/Context.js
// module id = 874
// module chunks = 1